import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"
import React from "react"
import { useT } from "@lib/i18n/client"
import { COLOR_ORDER, findShirtColor, isColorOption } from "@lib/shirt-colors"

type OptionSelectProps = {
  option: HttpTypes.StoreProductOption
  current: string | undefined
  updateOption: (title: string, value: string) => void
  title: string
  disabled: boolean
  "data-testid"?: string
}

const OptionSelect: React.FC<OptionSelectProps> = ({
  option,
  current,
  updateOption,
  title,
  "data-testid": dataTestId,
  disabled,
}) => {
  const t = useT()
  const isColor = isColorOption(title)

  const values = (option.values ?? []).map((v) => v.value)

  // Kolory pokazujemy w kolejności z katalogu (od jasnych do ciemnych),
  // nie w tej, w jakiej wpadły do bazy.
  const filteredOptions = isColor
    ? [...values].sort((a, b) => {
        const ia = COLOR_ORDER.indexOf(a)
        const ib = COLOR_ORDER.indexOf(b)
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
      })
    : values

  return (
    <div className="flex flex-col gap-y-3">
      <span className="text-base-semi text-ui-fg-base">
        {t(`Wybierz ${title.toLowerCase()}`)}
        {isColor && current && (
          <span className="ml-2 font-normal text-[color:var(--ateisci-gold)]">
            {t(current)}
          </span>
        )}
      </span>

      {isColor ? (
        <div className="flex flex-wrap gap-2" data-testid={dataTestId}>
          {filteredOptions.map((v) => {
            const shirt = findShirtColor(v)
            return (
              <button
                type="button"
                onClick={() => updateOption(option.id, v)}
                key={v}
                title={t(v)}
                aria-label={t(v)}
                aria-pressed={v === current}
                className={clx(
                  "flex h-10 w-10 items-center justify-center rounded-full border-2 p-[3px] transition-colors duration-150",
                  // Zaznaczenie na pierścieniu wokół próbki, a nie na jej
                  // obrysie: czarna próbka na czarnym tle nie miałaby jak
                  // pokazać, że jest wybrana. Pierścień działa też w modalu
                  // mobilnym, który ma jasne tło.
                  v === current
                    ? "border-[color:var(--ateisci-gold)]"
                    : "border-transparent hover:border-ui-border-strong",
                  disabled && "cursor-not-allowed opacity-40"
                )}
                disabled={disabled}
                data-testid="color-button"
              >
                <span
                  className="h-full w-full rounded-full border border-black/25 shadow-inner"
                  style={{ backgroundColor: shirt?.hex ?? "#888888" }}
                />
              </button>
            )
          })}
        </div>
      ) : (
        <div
          className="flex flex-wrap justify-between gap-2"
          data-testid={dataTestId}
        >
          {filteredOptions.map((v) => {
            return (
              <button
                onClick={() => updateOption(option.id, v)}
                key={v}
                className={clx(
                  "h-11 min-w-[3.25rem] flex-1 rounded-rounded border text-base font-medium transition-colors duration-150",
                  {
                    // Wybrany rozmiar wyraźnie złoty — na czerni sam obrys nie wystarczał.
                    "border-[color:var(--ateisci-gold)] bg-[rgba(216,178,90,0.14)] text-[color:var(--ateisci-gold)]":
                      v === current,
                    "border-ui-border-base bg-ui-bg-subtle text-ui-fg-base hover:border-[color:var(--ateisci-gold)] hover:bg-ui-bg-base":
                      v !== current,
                    "cursor-not-allowed opacity-40": disabled,
                  }
                )}
                disabled={disabled}
                data-testid="option-button"
              >
                {v}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default OptionSelect
